import { useCallback } from 'react';
import { Node } from 'reactflow';
import { nodesIntersect } from './utils';

interface UseNodeDragDropProps {
  nodes: Node[];
  updateNodeData: (nodeId: string, data: any) => void;
}

export function useNodeDragDrop({ nodes, updateNodeData }: UseNodeDragDropProps) {
  const handleNodeDragStop = useCallback(
    (_: any, draggedNode: Node) => {
      // Only process Page nodes being dragged
      if (draggedNode.type !== 'page') return;

      // Check if the dragged node intersects with any stub nodes
      const stubNode = nodes.find((n: Node) =>
        n.type === 'branchStub' &&
        n.id !== draggedNode.id &&
        nodesIntersect(draggedNode, n)
      );

      if (!stubNode) return;

      // Get stub data
      const { parentId, branchType, branchIndex } = stubNode.data;
      if (!parentId) return;

      // Find the parent page node
      const parentNode = nodes.find((n) => n.id === parentId);
      if (!parentNode || parentNode.data.type !== 'Page') return;

      // Find the branching element
      const branchingElement = (parentNode.data.elements || []).find((el: any) => el.createBranches === true);
      if (!branchingElement) return;

      // Update the branching element based on type
      let updatedElements = [...(parentNode.data.elements || [])];
      const elementIndex = updatedElements.findIndex((el: any) => el.id === branchingElement.id);

      if (elementIndex === -1) return;

      if (branchingElement.type === 'YesNo') {
        // Update YesNo branches
        const updatedBranches = { ...(branchingElement.branches || {}) };
        if (branchType === 'yes') {
          updatedBranches.yes = draggedNode.id;
        } else if (branchType === 'no') {
          updatedBranches.no = draggedNode.id;
        }

        updatedElements[elementIndex] = {
          ...branchingElement,
          branches: updatedBranches,
        };
      } else if (branchingElement.type === 'MultiChoice') {
        // Update MultiChoice branches
        const updatedBranches = [...(branchingElement.branches || [])];
        if (branchIndex !== undefined && updatedBranches[branchIndex]) {
          updatedBranches[branchIndex] = {
            ...updatedBranches[branchIndex],
            targetPageId: draggedNode.id,
          };
        }

        updatedElements[elementIndex] = {
          ...branchingElement,
          branches: updatedBranches,
        };
      } else if (branchingElement.type === 'Button') {
        updatedElements[elementIndex] = {
          ...branchingElement,
          targetPageId: draggedNode.id,
        };
      }

      // Update the parent node data
      updateNodeData(parentId, {
        ...parentNode.data,
        elements: updatedElements,
      });
    },
    [nodes, updateNodeData]
  );

  return {
    handleNodeDragStop,
  };
}
